import React from "react";
import logo from "../assets/EditedPng.png";

export default function Footer({ footerData }) {
  const {
    companyName = "Kerala Nest Constructions",
    companyDescription = "We are committed to delivering excellence through innovation, quality, and exceptional customer service.",
    contactInfo = {},
    socialMedia = {},
    quickLinks = [],
    copyrightText = `© ${new Date().getFullYear()} ${companyName || "Kerala Nest Constructions"
    }. All rights reserved.`,
    additionalLinks = [],
  } = footerData || {};

  const { address, phone, email } = contactInfo;

  const defaultQuickLinks = [
    { label: "Home", url: "#home" },
    { label: "About Us", url: "#about" },
    { label: "Projects", url: "#projects" },
    { label: "Career", url: "#careers" },
    { label: "Contact", url: "#contact" }
  ];

  const linksToShow = quickLinks.length > 0 ? quickLinks : defaultQuickLinks;

  return (
    <footer className="relative bg-white border-t border-gray-200 h-full border-b">
      <div className="container mx-auto px-4 md:px-6 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8  mt-6">
          {/* Company Info Section */}
          <div className="lg:col-span-2 lg:w-[430px]">

            <h3 className="text-gray-900 font-heading font-semibold text-lg mb-2 flex items-end gap-5"><img alt="Nest" src={logo} className="h-[70px]" />{companyName}</h3>
            <p className="text-slate-600 text-sm leading-relaxed mb-4">
              {companyDescription}
            </p>

            {/* Social Media */}
            {socialMedia && Object.values(socialMedia).some(Boolean) && (
              <div>
                <h4 className="text-gray-900 font-semibold text-sm mb-3">Follow Us</h4>
                <div className="flex gap-3">
                  {socialMedia.facebook && (
                    <a
                      href={socialMedia.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Facebook"
                      className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#1877F2] hover:border-[#1877F2] hover:text-white transition-all"
                    >
                      <i className="fab fa-facebook-f"></i>
                    </a>
                  )}
                  {socialMedia.instagram && (
                    <a
                      href={socialMedia.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#E4405F] hover:border-[#E4405F] hover:text-white transition-all"
                    >
                      <i className="fab fa-instagram"></i>
                    </a>
                  )}
                  {socialMedia.twitter && (
                    <a
                      href={socialMedia.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Twitter"
                      className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#1DA1F2] hover:border-[#1DA1F2] hover:text-white transition-all"
                    >
                      <i className="fab fa-twitter"></i>
                    </a>
                  )}
                  {socialMedia.linkedin && (
                    <a
                      href={socialMedia.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="LinkedIn"
                      className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#0A66C2] hover:border-[#0A66C2] hover:text-white transition-all"
                    >
                      <i className="fab fa-linkedin-in"></i>
                    </a>
                  )}
                  {socialMedia.youtube && (
                    <a
                      href={socialMedia.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="YouTube"
                      className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#FF0000] hover:border-[#FF0000] hover:text-white transition-all"
                    >
                      <i className="fab fa-youtube"></i>
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Quick Links Section */}
          <div>
            <h4 className="text-gray-900 font-heading font-semibold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {linksToShow.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.url}
                    className="text-slate-600 hover:text-secondary transition-colors text-sm font-semibold"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Information Section */}
          <div>
            <h4 className="text-gray-900 font-heading font-semibold text-lg mb-4">Contact Info</h4>
            <div className="space-y-3">
              {phone && (
                <div className="flex items-start gap-3">
                  <i className="fas fa-phone text-secondary mt-1"></i>
                  <a href={`tel:${phone}`} className="text-slate-600 hover:text-secondary transition-colors text-sm">
                    {phone}
                  </a>
                </div>
              )}
              {email && (
                <div className="flex items-start gap-3">
                  <i className="fas fa-envelope text-secondary mt-1"></i>
                  <a href={`mailto:${email}`} className="text-slate-600 hover:text-secondary transition-colors text-sm">
                    {email}
                  </a>
                </div>
              )}
              {address && (
                <div className="flex items-start gap-3">
                  <i className="fas fa-map-marker-alt text-secondary mt-1"></i>
                  <span className="text-slate-600 text-sm">{address}</span>
                </div>
              )}
            </div>
          </div>

          {/* Additional Links Section */}
          {additionalLinks.length > 0 && (
            <div>
              <h4 className="text-gray-900 font-heading font-semibold text-lg mb-4">Resources</h4>
              <ul className="space-y-2">
                {additionalLinks.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.url}
                      className="text-slate-600 hover:text-secondary transition-colors text-sm"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer Bottom */}
        <div className="pt-8 border-t border-gray-200 mt-2 ">
          <div className="text-center">
            <p className="text-slate-500 text-sm">{copyrightText}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
